import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isSpeechSynthesisSupported,
  lectureContentToSpeechText,
  pickPreferredVoice,
  splitSpeechChunks,
} from '../utils/lectureTts';

/**
 * Browser TTS for lecture content. Stops automatically when `resetKey` changes.
 */
export default function useLectureTts({ text, resetKey, enabled = true }) {
  const supported = isSpeechSynthesisSupported();
  const [status, setStatus] = useState('idle'); // idle | speaking | paused
  const [error, setError] = useState(null);
  const chunkIndexRef = useRef(0);
  const chunksRef = useRef([]);
  const voiceRef = useRef(null);
  const cancelledRef = useRef(false);

  const stopInternal = useCallback(() => {
    cancelledRef.current = true;
    chunkIndexRef.current = 0;
    chunksRef.current = [];
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* ignore */
    }
    setStatus('idle');
  }, []);

  const loadVoice = useCallback(() => {
    if (!supported) return null;
    if (voiceRef.current) return voiceRef.current;
    const voices = window.speechSynthesis.getVoices() || [];
    voiceRef.current = pickPreferredVoice(voices);
    return voiceRef.current;
  }, [supported]);

  useEffect(() => {
    if (!supported) return undefined;
    loadVoice();
    const onVoices = () => {
      voiceRef.current = null;
      loadVoice();
    };
    window.speechSynthesis.addEventListener?.('voiceschanged', onVoices);
    // Chrome sometimes only populates voices after getVoices() + event
    window.speechSynthesis.getVoices();
    return () => {
      window.speechSynthesis.removeEventListener?.('voiceschanged', onVoices);
    };
  }, [supported, loadVoice]);

  // Stop when lecture changes or content disabled
  useEffect(() => {
    stopInternal();
    setError(null);
  }, [resetKey, stopInternal]);

  useEffect(() => () => stopInternal(), [stopInternal]);

  const speakFrom = useCallback(
    (startIndex = 0) => {
      if (!supported || !enabled) {
        setError('Text-to-speech is not supported in this browser.');
        return;
      }
      const speakable = lectureContentToSpeechText(text);
      if (!speakable) {
        setError('Nothing to read yet. Wait for the lesson to finish loading.');
        return;
      }

      cancelledRef.current = false;
      setError(null);
      const chunks = splitSpeechChunks(speakable);
      chunksRef.current = chunks;
      chunkIndexRef.current = startIndex;

      const speakChunk = (index) => {
        if (cancelledRef.current) return;
        if (index >= chunks.length) {
          setStatus('idle');
          chunkIndexRef.current = 0;
          return;
        }
        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        utterance.rate = 0.95;
        // Slight lift helps some OS default voices read more clearly as a teaching tone
        utterance.pitch = 1.05;
        utterance.volume = 1;
        const voice = loadVoice();
        if (voice) utterance.voice = voice;
        if (voice?.lang) utterance.lang = voice.lang;
        else utterance.lang = 'en-US';

        utterance.onstart = () => setStatus('speaking');
        utterance.onend = () => {
          if (cancelledRef.current) return;
          chunkIndexRef.current = index + 1;
          speakChunk(index + 1);
        };
        utterance.onerror = (event) => {
          if (cancelledRef.current || event?.error === 'interrupted' || event?.error === 'canceled') {
            return;
          }
          setStatus('idle');
          setError('Could not play speech. Try again.');
        };

        try {
          // Chrome bug: resume before speak after cancel
          window.speechSynthesis.resume();
          window.speechSynthesis.speak(utterance);
          setStatus('speaking');
        } catch {
          setError('Could not start speech.');
          setStatus('idle');
        }
      };

      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
      // Small delay helps Chrome after cancel()
      window.setTimeout(() => speakChunk(startIndex), 40);
    },
    [supported, enabled, text, loadVoice]
  );

  const play = useCallback(() => {
    if (status === 'paused') {
      try {
        window.speechSynthesis.resume();
        setStatus('speaking');
        return;
      } catch {
        /* fall through to restart */
      }
    }
    speakFrom(0);
  }, [status, speakFrom]);

  const pause = useCallback(() => {
    if (!supported) return;
    try {
      window.speechSynthesis.pause();
      setStatus('paused');
    } catch {
      setError('Pause is not supported in this browser.');
    }
  }, [supported]);

  const stop = useCallback(() => {
    stopInternal();
  }, [stopInternal]);

  return {
    supported,
    status,
    error,
    play,
    pause,
    stop,
    canPlay: Boolean(enabled && text && lectureContentToSpeechText(text)),
  };
}
